import {
  Box,
  Card,
  CardHeader,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
} from '@mui/material';
import {
  FC, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Chart, { useChart, ApexOptions } from '@mgm/common/components/charts';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getIsOrdersLoading } from '../../../../store/orders/selectors';
import { AnimatedDivider } from '../../../../components';

type TTeamStat = {
  id: number;
  name: string;
  netSales: number;
  commission: number;
};

type TTeamComparisonChart = {
  teams: TTeamStat[];
};

const TeamComparisonChart: FC<TTeamComparisonChart> = ({ teams }) => {
  const { t } = useTranslation(['commissions', 'common']);
  const chartRef = useRef<any>(null);
  const isOrdersLoading = useAppSelector(getIsOrdersLoading);

  const [metric, setMetric] = useState<'netSales' | 'commission'>('netSales');

  const handleChange = (event: SelectChangeEvent<'netSales' | 'commission'>) => {
    setMetric(event.target.value as 'netSales' | 'commission');
  };

  const chartData = useMemo(() => {
    const categories = teams.map((team) => team.name || 'Unnamed Team');
    const seriesData = teams.map((team) => (metric === 'netSales' ? team.netSales : team.commission));
    return { categories, seriesData };
  }, [teams, metric]);

  const chartOptions: ApexOptions = useChart({
    chart: {
      type: 'bar',
      events: {
        mounted: (chart) => {
          chartRef.current = chart;
        },
      },
    },
    xaxis: {
      categories: chartData.categories,
      labels: {
        rotate: -45,
        style: {
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      title: {
        text: metric === 'netSales' ? t('netSales') : t('commission'),
      },
      labels: {
        formatter: (value: number) => `$${value.toFixed(2)}`,
      },
    },
    tooltip: {
      shared: false,
      intersect: true,
      y: {
        formatter: (value: number) => `$${value.toFixed(2)}`,
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: '40%',
        distributed: true,
      },
    },
    colors: [
      '#8a8efa',
      '#A4F7CC',
      '#00B8D9',
      '#FF5630',
      '#36B37E',
      '#B76E00',
      '#A855F7',
      '#006C9C',
      '#003768',
      '#aa2d52',
      '#F7D2FF',
      '#e089e9',
      '#5c671d',
      '#CCFAFF',
      '#FFAB00',
      '#FFBD98',
      '#60F1C8',
      '#83CFFF',
      '#FF8F6D',
      '#0A5554',
      '#868678',
      '#93ea7e',
      '#a75a57',
      '#562e5b',
      '#4a599f',
      '#212B36',
      '#581C87',
    ],
  });

  const validSeries = [
    {
      name: metric === 'netSales' ? t('netSales') : t('commission'),
      data: chartData.seriesData,
    },
  ];

  return (
    <Card>
      <CardHeader
        sx={{ p: 2 }}
        title="Team Comparison"
        action={(
          <FormControl size="small">
            <Select value={metric} onChange={handleChange}>
              <MenuItem value="netSales">{t('netSales')}</MenuItem>
              <MenuItem value="commission">{t('commission')}</MenuItem>
            </Select>
          </FormControl>
        )}
      />
      <AnimatedDivider loading={isOrdersLoading} />
      <Box sx={{ mx: 3 }} dir="ltr">
        {/* eslint-disable-next-line no-nested-ternary */}
        {isOrdersLoading ? (
          <Box pt={3}>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i?.toString()} variant="rectangular" height={40} sx={{ mb: 2 }} />
            ))}
          </Box>
        ) : chartData.categories.length ? (
          <Chart
            type="bar"
            series={validSeries}
            options={chartOptions}
            height={400}
          />
        ) : (
          <CustomNoRowsOverlay text={t('noDataForToday', { ns: 'common' })} />
        )}
      </Box>
    </Card>
  );
};

export { TeamComparisonChart };
